import React from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import LoadingState from '../../dashboard/LoadingState';
import ErrorState from '../../dashboard/ErrorState';
import PermissionDenied from '../../dashboard/PermissionDenied';
import CampaignAdCard from '../CampaignAdCard';
import CampaignLeadStats from '../CampaignLeadStats';
import { useCampaignQuery } from '../useCampaignQuery';

/**
 * Campaign detail body, shared by the dashboard (/dashboard/campaigns/:uuid)
 * and mobile (/mobile/campaigns/:uuid) pages: each page only adds its own
 * shell. `renderActions(campaign)` returns the page's buttons (e.g. "View
 * leads"), rendered at the end of the ad card.
 */
export default function CampaignDetailContent({ uuid, renderActions }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useCampaignQuery(uuid);

  if (isLoading) {
    return <LoadingState message={t('campaigns.states.loading')} />;
  }

  if (isError) {
    const status = error?.response?.status;
    if (status === 403) {
      return (
        <PermissionDenied
          title={t('campaigns.states.permissionDeniedTitle')}
          message={t('campaigns.states.permissionDeniedMessage')}
        />
      );
    }
    if (status === 404) {
      return (
        <ErrorState
          title={t('campaigns.states.notFoundTitle')}
          message={t('campaigns.states.notFoundMessage')}
        />
      );
    }
    return (
      <ErrorState
        title={t('campaigns.states.errorTitle')}
        message={t('campaigns.states.errorMessage')}
        actionLabel={t('campaigns.states.retry')}
        onRetry={refetch}
      />
    );
  }

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <CampaignAdCard campaign={data} actions={renderActions?.(data)} />
      <CampaignLeadStats campaign={data} />
    </Stack>
  );
}
